<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";
import { getObservationList, getSiteDetail, getSpecieDetail } from "@carnet/api/backoffice";
import { apiBool, throwOnNotFound } from "@/utils/api";
import { apiRadiusKm } from "@carnet/ui/utils/location";
import { firstQueryValue } from "@carnet/ui/utils/observationPhotosQuery";

definePageMeta({
  name: "observations.specie",
  layout: "app",
});

const { t } = useI18n();
const localePath = useLocalePath();
const { loggedIn } = useUserSession();
const route = useRoute("observations.specie");

const specieId = computed(() => Number(route.params.specieId));

const siteIdQuery = computed(() => firstQueryValue(route.query.siteId));

const { data: siteData } = await useAsyncData(
  "observations-specie-site",
  async () => {
    if (!siteIdQuery.value) return null;
    const res = await getSiteDetail({
      composable: "$fetch",
      path: { locale: appLocale(), id: siteIdQuery.value },
      headers: { Accept: "application/json" },
    });
    return res.data ?? null;
  },
  { watch: [siteIdQuery] }
);

const site = computed(() => siteData.value);

const page = useUrlPage();

const mineOnly = useMineFilter();
const { filter: locationFilter, chip: locationChip } = useUrlLocationFilter({ withRadius: true });

const { data: specieData, error: specieError } = await getSpecieDetail({
  composable: "useFetch",
  key: `observations-specie-${specieId.value}`,
  path: { locale: appLocale(), id: String(specieId.value) },
});

throwOnNotFound(specieError.value, "Espèce introuvable");

const specie = computed(() => specieData.value?.data);

const {
  data: observationsData,
  status,
  error,
  refresh,
} = await getObservationList({
  composable: "useLazyFetch",
  key: `observations-by-specie-${specieId.value}`,
  path: { locale: appLocale() },
  query: computed(() => ({
    page: page.value,
    sort: "date_desc" as const,
    size: 18,
    specieId: specieId.value,
    withPhotoOnly: apiBool(true),
    siteId: siteIdQuery.value ? Number(siteIdQuery.value) : undefined,
    mineOnly: apiBool(mineOnly.value),
    "center[latitude]": locationFilter.value.center?.[1],
    "center[longitude]": locationFilter.value.center?.[0],
    radius: apiRadiusKm(locationFilter.value),
  })),
});

const observations = computed(() => observationsData.value?.data?.list ?? []);
const pagination = computed(() => observationsData.value?.data?.pagination);
const loading = computed(() => status.value === "idle" || status.value === "pending");

const { title, breadcrumbLabel } = usePageTitle({ base: () => specie.value?.commonName ?? t("observations.title"), page });

useSeoMeta({
  title: () => formatPageTitle(title.value, t("observations.title")),
  description: () => t("seo.observations.specie.description", { name: specie.value?.commonName ?? t("species.title") }),
});
useSeoImage(() => seoImageFrom(specie.value?.mainImage, specie.value?.commonName ?? t("observations.title")));

const breadcrumb = computed<BreadcrumbItem[]>(() => [
  { label: t("home.title"), to: localePath({ name: "index" }) },
  { label: t("observations.title"), to: localePath({ name: "observations" }) },
  { label: breadcrumbLabel.value },
]);

function photoIndex(tileIndex: number): number {
  const size = pagination.value?.size ?? observations.value.length;
  return (page.value - 1) * size + tileIndex + 1;
}
</script>

<template>
  <div class="flex flex-col grow bg-white">
    <AppSection :ui="{ base: 'pt-8 pb-10' }">
      <div class="flex items-start justify-between gap-6">
        <WebBreadcrumbs :items="breadcrumb" />
      </div>
    </AppSection>

    <p role="status" aria-live="polite" aria-atomic="true" class="sr-only">
      <template v-if="loading">{{ t("ui.loading") }}</template>
      <template v-else-if="observations.length">{{ t("observations.count", pagination?.total ?? observations.length) }}</template>
    </p>

    <div v-if="specieError" class="px-10 py-8 flex flex-col items-center gap-y-4">
      <AppMessageWrapper type="error">{{ t("error.loading") }}</AppMessageWrapper>
    </div>

    <AppSection v-else-if="specie" :ui="{ base: 'pb-12' }">
      <header class="flex flex-col mb-12">
        <p class="sm:text-lg font-semibold uppercase text-primary mb-3">
          {{ t("observations.species.photos.title") }}
        </p>
        <h1 class="text-4xl sm:text-5xl md:text-6xl font-semibold text-primary not-last:mb-4">
          {{ specie.commonName }}
          <i v-if="specie.scientificName" class="italic font-normal">({{ specie.scientificName }})</i>
        </h1>

        <div v-if="site || locationChip || (mineOnly && loggedIn)" class="flex gap-x-10 gap-y-6 flex-wrap">
          <div v-if="site" class="flex items-baseline gap-2.5">
            <span class="text-base font-semibold text-primary">{{ t("ui.site.title") }}</span>
            <UBadge variant="soft" size="lg"
              >{{ site.name }}<template v-if="site.nearbyCityLabel"> – {{ site.nearbyCityLabel }}</template></UBadge
            >
          </div>

          <div v-if="locationChip" class="flex items-baseline gap-2.5">
            <span class="text-base font-semibold text-primary">{{ t("ui.location.title") }}</span>
            <UBadge variant="soft" size="lg">{{ locationChip.label }}</UBadge>
          </div>

          <div v-if="mineOnly && loggedIn" class="flex items-baseline gap-2.5">
            <span class="text-base font-semibold text-primary">{{ t("ui.filters.title") }}</span>
            <UBadge variant="soft" size="lg">{{ t("observations.filters.myObservations") }}</UBadge>
          </div>
        </div>
      </header>

      <WebPaginatedList
        v-model:page="page"
        :items="observations"
        :pagination="pagination"
        :loading="loading"
        :error="error"
        :skeleton-count="18"
        list-class="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12"
        @refresh="refresh()"
      >
        <template #skeleton><AppTileObservationPhotoSkeleton /></template>
        <template #default="{ item, index }">
          <AppTileObservationPhoto
            :observation="item"
            :to="
              $localePath({ name: 'observations.specie.photo', params: { specieId, photoIndex: photoIndex(index) }, query: route.query })
            "
            :alt="specie?.commonName ?? ''"
          />
        </template>
        <template #empty>
          <p class="text-blue-600 pt-10">{{ t("observations.species.photos.none") }}</p>
        </template>
      </WebPaginatedList>
    </AppSection>
  </div>
</template>
